import { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus,
  GraduationCap,
  Award,
  CheckCircle2,
  XCircle,
  Trash2,
  Play,
  Users,
  Sparkles,
  BookOpen,
  Video,
  Pencil,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatDateTime } from "@/lib/format";
import VisibilityPicker from "@/components/VisibilityPicker";
import AuthImage from "@/components/AuthImage";

async function downloadCertificate(t, userId, userName) {
  try {
    const params = userId ? { user_id: userId } : {};
    const res = await api.get(`/trainings/${t.id}/certificate.pdf`, {
      params,
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `certificado-${(userName || "meu-certificado").replace(/\s+/g, "_")}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Certificado gerado em PDF");
  } catch (err) {
    toast.error(err?.response?.data?.detail || "Falha ao gerar certificado");
  }
}

const emptyQuiz = () => ({
  id: crypto.randomUUID(),
  text: "",
  options: ["", ""],
  correct_index: 0,
});

const emptyTraining = () => ({
  title: "",
  description: "",
  content: "",
  photo_urls: [],
  video_url: "",
  quiz: [emptyQuiz()],
  passing_score: 60,
  assigned_roles: [],
  assigned_user_ids: [],
});

export default function Trainings() {
  const { user } = useAuth();
  const [trainings, setTrainings] = useState([]);
  const [openEditor, setOpenEditor] = useState(false);
  const [openRunner, setOpenRunner] = useState(null); // training
  const [openCompletions, setOpenCompletions] = useState(null); // training
  const [openManuals, setOpenManuals] = useState(false);
  const [form, setForm] = useState(emptyTraining());
  const [editingId, setEditingId] = useState(null);
  const [users, setUsers] = useState([]);

  const isAdmin = ["super_admin", "company_admin", "manager", "supervisor"].includes(
    user?.role
  );

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/trainings");
      setTrainings(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar");
    }
  }, []);

  useEffect(() => {
    load();
    if (isAdmin) {
      api
        .get("/users")
        .then(({ data }) => setUsers(data))
        .catch((err) => console.error("Failed to load users", err));
    }
  }, [load, isAdmin]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyTraining());
    setOpenEditor(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      title: t.title,
      description: t.description || "",
      content: t.content || "",
      photo_urls: t.photo_urls || [],
      video_url: t.video_url || "",
      quiz: t.quiz?.length ? t.quiz : [emptyQuiz()],
      passing_score: t.passing_score || 60,
      assigned_roles: t.assigned_roles || [],
      assigned_user_ids: t.assigned_user_ids || [],
    });
    setOpenEditor(true);
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Título obrigatório");
      return;
    }
    // Sanitize quiz — drop empty questions/options
    const quiz = (form.quiz || [])
      .filter((q) => q.text.trim())
      .map((q) => ({
        id: q.id,
        text: q.text.trim(),
        options: (q.options || []).map((o) => o.trim()).filter(Boolean),
        correct_index: q.correct_index || 0,
      }));
    const payload = { ...form, quiz };
    try {
      if (editingId) {
        await api.patch(`/trainings/${editingId}`, payload);
        toast.success("Treinamento atualizado");
      } else {
        await api.post("/trainings", payload);
        toast.success("Treinamento criado");
      }
      setOpenEditor(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    }
  };

  const remove = async (id) => {
    if (!confirm("Excluir este treinamento?")) return;
    try {
      await api.delete(`/trainings/${id}`);
      toast.success("Removido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    }
  };

  const setQuizField = (idx, patch) => {
    setForm((f) => {
      const quiz = [...f.quiz];
      quiz[idx] = { ...quiz[idx], ...patch };
      return { ...f, quiz };
    });
  };

  const addQuiz = () => setForm((f) => ({ ...f, quiz: [...f.quiz, emptyQuiz()] }));
  const removeQuiz = (idx) =>
    setForm((f) => ({ ...f, quiz: f.quiz.filter((_, i) => i !== idx) }));

  const stats = useMemo(() => {
    const done = trainings.filter((t) => t._my_completion?.acknowledged).length;
    return { total: trainings.length, done, pending: trainings.length - done };
  }, [trainings]);

  return (
    <Layout
      title="Treinamento"
      subtitle="Conteúdos + quiz obrigatório com registro de ciência dos colaboradores"
      actions={
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="border-stone-300 bg-white"
            onClick={() => setOpenManuals(true)}
            data-testid="training-manuals-btn"
          >
            <BookOpen className="h-4 w-4 mr-2" /> Manual de boas práticas
          </Button>
          {isAdmin && (
            <Button
              onClick={openNew}
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
              data-testid="training-new-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Novo treinamento
            </Button>
          )}
        </div>
      }
    >
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { label: "Total", value: stats.total, tone: "border-stone-200 bg-white text-stone-700" },
          { label: "Concluídos por mim", value: stats.done, tone: "border-emerald-200 bg-emerald-50 text-emerald-900" },
          { label: "Pendentes", value: stats.pending, tone: "border-amber-200 bg-amber-50 text-amber-900" },
        ].map((k) => (
          <Card key={k.label} className={`p-4 rounded-xl border ${k.tone}`}>
            <div className="text-[10px] uppercase tracking-[0.18em] font-body opacity-80">{k.label}</div>
            <div className="font-display text-3xl font-extrabold mt-1">{k.value}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="training-list">
        {trainings.length === 0 && (
          <Card className="col-span-full p-10 rounded-xl border-stone-200 bg-white text-center text-stone-500">
            <GraduationCap className="h-8 w-8 mx-auto mb-2 text-stone-400" />
            Nenhum treinamento cadastrado ainda.
          </Card>
        )}
        {trainings.map((t) => {
          const comp = t._my_completion;
          const acknowledged = comp?.acknowledged;
          return (
            <Card key={t.id} className="p-5 rounded-xl border-stone-200 bg-white flex flex-col" data-testid={`training-card-${t.id}`}>
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-md bg-emerald-50 text-emerald-800 flex items-center justify-center shrink-0">
                  <GraduationCap className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold tracking-tight text-stone-950">
                    {t.title}
                  </div>
                  <p className="text-xs text-stone-500 font-body mt-0.5 line-clamp-2">
                    {t.description || "Sem descrição"}
                  </p>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <Badge variant="outline" className="border-stone-300 text-[10px]">
                  {t.quiz?.length || 0} perguntas
                </Badge>
                {t.video_url && (
                  <Badge variant="outline" className="border-blue-300 text-blue-800 bg-blue-50 text-[10px]">
                    <Video className="h-3 w-3 mr-1" /> vídeo
                  </Badge>
                )}
                {(t.photo_urls?.length || 0) > 0 && (
                  <Badge variant="outline" className="border-stone-300 text-[10px]">
                    {t.photo_urls.length} foto(s)
                  </Badge>
                )}
                {acknowledged ? (
                  <Badge className="bg-emerald-600 text-white border-none text-[10px]">
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Ciência ok · {comp.score}%
                  </Badge>
                ) : comp ? (
                  <Badge className="bg-amber-100 text-amber-900 border-amber-200 border text-[10px]">
                    Refazer · último score {comp.score}%
                  </Badge>
                ) : (
                  <Badge className="bg-stone-100 text-stone-700 border-none text-[10px]">
                    Não iniciado
                  </Badge>
                )}
              </div>
              <div className="mt-4 flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  onClick={() => setOpenRunner(t)}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid={`training-open-${t.id}`}
                >
                  <Play className="h-3.5 w-3.5 mr-1" /> {acknowledged ? "Rever" : "Iniciar"}
                </Button>
                {acknowledged && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-emerald-300 text-emerald-800 bg-white"
                    onClick={() => downloadCertificate(t)}
                    data-testid={`training-certificate-${t.id}`}
                  >
                    <Award className="h-3.5 w-3.5 mr-1" /> Certificado
                  </Button>
                )}
                {isAdmin && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setOpenCompletions(t)}
                      data-testid={`training-completions-${t.id}`}
                    >
                      <Users className="h-3.5 w-3.5 mr-1" /> Ciências
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(t)}
                    >
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-rose-600"
                      onClick={() => remove(t.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Editor dialog */}
      <TrainingEditor
        open={openEditor}
        onOpenChange={setOpenEditor}
        form={form}
        setForm={setForm}
        onSave={save}
        setQuizField={setQuizField}
        addQuiz={addQuiz}
        removeQuiz={removeQuiz}
        isEditing={!!editingId}
        users={users}
      />

      {/* Runner dialog */}
      {openRunner && (
        <TrainingRunner
          training={openRunner}
          onClose={(refresh) => {
            setOpenRunner(null);
            if (refresh) load();
          }}
        />
      )}

      {/* Completions dialog */}
      {openCompletions && (
        <TrainingCompletionsDialog
          training={openCompletions}
          onClose={() => setOpenCompletions(null)}
        />
      )}

      {/* Best Practices Manuals dialog */}
      <BestPracticesDialog
        open={openManuals}
        onOpenChange={setOpenManuals}
        isAdmin={isAdmin}
        canAI={["super_admin", "company_admin", "manager"].includes(user?.role)}
      />
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------
function TrainingEditor({ open, onOpenChange, form, setForm, onSave, setQuizField, addQuiz, removeQuiz, isEditing, users = [] }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isEditing ? "Editar treinamento" : "Novo treinamento"}
          </DialogTitle>
          <DialogDescription className="font-body text-xs text-stone-500">
            Regras da empresa, boas práticas, procedimentos. O colaborador só dá "ciência"
            após acertar o quiz (nota mínima configurável).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Título *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1 bg-white"
              placeholder="Ex: Higienização de câmara fria"
              data-testid="training-title"
              autoFocus
            />
          </div>
          <div>
            <Label>Descrição breve</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 bg-white"
              placeholder="Uma frase para a lista"
            />
          </div>
          <div>
            <Label>Conteúdo do treinamento (markdown/texto)</Label>
            <Textarea
              rows={8}
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              className="mt-1 bg-white font-mono text-sm"
              placeholder="Descreva o procedimento, regras e exemplos..."
              data-testid="training-content"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>URL do vídeo (YouTube/Vimeo)</Label>
              <Input
                value={form.video_url}
                onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                className="mt-1 bg-white"
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </div>
            <div>
              <Label>Nota mínima para ciência (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={form.passing_score}
                onChange={(e) =>
                  setForm({ ...form, passing_score: Number(e.target.value) })
                }
                className="mt-1 bg-white"
              />
            </div>
          </div>
          <VisibilityPicker
            form={form}
            setForm={setForm}
            users={users}
            testPrefix="training-visibility"
            description="Quem verá este treinamento."
          />
          <div>
            <Label>Fotos do treinamento</Label>
            {/* FASE 20 — direct photo upload */}
            <div className="mt-1 flex items-center gap-2">
              <input
                id="training-photo-input"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (!f) return;
                  if (f.size > 5 * 1024 * 1024) {
                    toast.error("Imagem maior que 5MB");
                    return;
                  }
                  try {
                    const fd = new FormData();
                    fd.append("file", f);
                    const { data } = await api.post("/files/upload", fd, {
                      headers: { "Content-Type": "multipart/form-data" },
                    });
                    setForm((prev) => ({
                      ...prev,
                      photo_urls: [...(prev.photo_urls || []), data.url],
                    }));
                    toast.success("Foto adicionada");
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Falha no upload");
                  }
                }}
                data-testid="training-photo-input"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => document.getElementById("training-photo-input")?.click()}
                data-testid="training-photo-upload"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Enviar foto
              </Button>
              <span className="text-[11px] text-stone-500 font-body">
                {(form.photo_urls || []).length} foto(s)
              </span>
            </div>
            {(form.photo_urls || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {form.photo_urls.map((u, i) => (
                  <div key={u || i} className="relative">
                    <AuthImage src={u} alt={`foto ${i + 1}`} className="h-16 w-16 rounded-md object-cover border border-stone-200" />
                    <button
                      type="button"
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          photo_urls: prev.photo_urls.filter((_, idx) => idx !== i),
                        }))
                      }
                      className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-rose-600 text-white text-[9px] flex items-center justify-center"
                      data-testid={`training-photo-remove-${i}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Textarea
              rows={2}
              value={(form.photo_urls || []).join("\n")}
              onChange={(e) =>
                setForm({
                  ...form,
                  photo_urls: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
                })
              }
              className="mt-2 bg-white font-mono text-xs"
              placeholder="Ou cole URLs de fotos (uma por linha)"
            />
          </div>

          <div className="pt-2 border-t border-stone-200">
            <div className="flex items-center justify-between mb-2">
              <Label className="font-display font-bold">Quiz obrigatório</Label>
              <Button size="sm" variant="outline" onClick={addQuiz} data-testid="training-quiz-add">
                <Plus className="h-3.5 w-3.5 mr-1" /> Pergunta
              </Button>
            </div>
            {(form.quiz || []).map((q, idx) => (
              <Card key={q.id} className="p-3 mb-2 rounded-md bg-stone-50 border-stone-200">
                <div className="flex items-start gap-2 mb-2">
                  <span className="font-display font-bold text-sm w-6 text-stone-500">#{idx + 1}</span>
                  <Input
                    value={q.text}
                    onChange={(e) => setQuizField(idx, { text: e.target.value })}
                    className="flex-1 bg-white"
                    placeholder="Pergunta"
                    data-testid={`training-q-text-${idx}`}
                  />
                  {form.quiz.length > 1 && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => removeQuiz(idx)}
                      className="text-rose-600 h-9 w-9"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                <div className="space-y-1.5 pl-8">
                  {(q.options || []).map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={q.correct_index === oi}
                        onChange={() => setQuizField(idx, { correct_index: oi })}
                        className="accent-emerald-700"
                        data-testid={`training-q-${idx}-correct-${oi}`}
                      />
                      <Input
                        value={opt}
                        onChange={(e) => {
                          const options = [...q.options];
                          options[oi] = e.target.value;
                          setQuizField(idx, { options });
                        }}
                        className="bg-white text-sm h-8"
                        placeholder={`Opção ${oi + 1}`}
                      />
                      {q.options.length > 2 && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => {
                            const options = q.options.filter((_, x) => x !== oi);
                            setQuizField(idx, {
                              options,
                              correct_index: Math.min(q.correct_index, options.length - 1),
                            });
                          }}
                          className="text-rose-500 h-8 w-8"
                        >
                          <XCircle className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setQuizField(idx, { options: [...q.options, ""] })
                    }
                    className="text-xs h-7 text-emerald-800"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Adicionar opção
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={onSave}
            className="bg-emerald-800 hover:bg-emerald-900 text-white"
            data-testid="training-save"
          >
            {isEditing ? "Salvar" : "Publicar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Runner (operator takes the training + quiz)
// ---------------------------------------------------------------------------
function TrainingRunner({ training, onClose }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const quiz_responses = training.quiz.map((q) => ({
        q_id: q.id,
        selected_index: answers[q.id] ?? -1,
      }));
      const { data } = await api.post(
        `/trainings/${training.id}/acknowledge`,
        { quiz_responses }
      );
      setResult(data);
      if (data.passed) {
        toast.success(`Ciência registrada! ${data.score}%`);
      } else {
        toast.error(`Nota ${data.score}% abaixo do mínimo. Refaça.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    } finally {
      setSubmitting(false);
    }
  };

  const embed = getVideoEmbed(training.video_url);

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose(result?.passed)}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-emerald-800" /> {training.title}
          </DialogTitle>
          <DialogDescription className="font-body text-xs text-stone-500">
            Leia todo o conteúdo e responda o quiz para registrar sua ciência.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {training.description && (
            <p className="text-sm text-stone-700 font-body">{training.description}</p>
          )}
          {training.content && (
            <Card className="p-4 rounded-md bg-stone-50 border-stone-200">
              <pre className="whitespace-pre-wrap font-body text-sm text-stone-800">
                {training.content}
              </pre>
            </Card>
          )}
          {embed && (
            <div className="aspect-video rounded-md overflow-hidden bg-stone-950">
              <iframe
                src={embed}
                title="Video"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          {(training.photo_urls || []).length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {training.photo_urls.map((u, i) => (
                <AuthImage
                  key={u || i}
                  src={u}
                  alt={`foto ${i + 1}`}
                  className="rounded-md object-cover h-32 w-full border border-stone-200"
                />
              ))}
            </div>
          )}

          {training.quiz?.length > 0 && (
            <div className="pt-2 border-t border-stone-200">
              <h3 className="font-display font-bold tracking-tight mb-3">
                Quiz obrigatório
              </h3>
              {training.quiz.map((q, idx) => (
                <Card key={q.id} className="p-3 mb-2 rounded-md border-stone-200 bg-white" data-testid={`quiz-q-${idx}`}>
                  <div className="font-display font-semibold text-sm mb-2">
                    #{idx + 1} {q.text}
                  </div>
                  <div className="space-y-1">
                    {q.options.map((opt, oi) => (
                      <label
                        key={`${oi}-${opt}`}
                        className="flex items-center gap-2 text-sm font-body cursor-pointer hover:bg-stone-50 px-2 py-1 rounded"
                      >
                        <input
                          type="radio"
                          name={`q-${q.id}`}
                          checked={answers[q.id] === oi}
                          onChange={() => setAnswers((a) => ({ ...a, [q.id]: oi }))}
                          disabled={!!result}
                          className="accent-emerald-700"
                          data-testid={`quiz-answer-${idx}-${oi}`}
                        />
                        <span
                          className={
                            result && oi === q.correct_index
                              ? "text-emerald-700 font-semibold"
                              : ""
                          }
                        >
                          {opt}
                        </span>
                      </label>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {result && (
            <Card
              className={`p-4 rounded-md border-2 ${
                result.passed
                  ? "border-emerald-300 bg-emerald-50/60"
                  : "border-amber-300 bg-amber-50/60"
              }`}
              data-testid="training-result"
            >
              <div className="flex items-center gap-2">
                {result.passed ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-700" />
                ) : (
                  <XCircle className="h-5 w-5 text-amber-700" />
                )}
                <div className="font-display font-bold">
                  {result.passed
                    ? `Aprovado com ${result.score}% — ciência registrada!`
                    : `Nota ${result.score}% — refaça para dar ciência.`}
                </div>
              </div>
            </Card>
          )}
        </div>
        <DialogFooter>
          {!result && training.quiz?.length > 0 && (
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
              data-testid="training-submit"
            >
              {submitting ? "Enviando…" : "Enviar quiz e dar ciência"}
            </Button>
          )}
          <Button variant="outline" onClick={() => onClose(!!result?.passed)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Admin: view completions
// ---------------------------------------------------------------------------
function TrainingCompletionsDialog({ training, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/trainings/${training.id}/completions`);
        setItems(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Falha");
      } finally {
        setLoading(false);
      }
    })();
  }, [training.id]);

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">
            Ciências: {training.title}
          </DialogTitle>
          <DialogDescription className="font-body text-xs text-stone-500">
            {items.length} colaboradore(s) registraram participação.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-96 overflow-y-auto divide-y divide-stone-100">
          {loading && (
            <div className="text-center py-10 text-stone-400 font-body text-sm">
              Carregando…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="text-center py-10 text-stone-500 font-body text-sm">
              Ninguém completou este treinamento ainda.
            </div>
          )}
          {items.map((c) => (
            <div key={c.id} className="py-2.5 flex items-center gap-3" data-testid={`completion-${c.id}`}>
              <div
                className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${
                  c.passed
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-amber-100 text-amber-800"
                }`}
              >
                {c.passed ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-sm">{c.user_name}</div>
                <div className="text-xs text-stone-500 font-body">
                  {formatDateTime(c.completed_at)}
                </div>
              </div>
              <Badge
                variant="outline"
                className={c.passed ? "border-emerald-300 text-emerald-800" : "border-amber-300 text-amber-800"}
              >
                {c.score}%
              </Badge>
              {c.acknowledged && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-emerald-300 text-emerald-800 bg-white"
                  onClick={() => downloadCertificate({ id: training.id }, c.user_id, c.user_name)}
                  data-testid={`completion-certificate-${c.id}`}
                >
                  <Award className="h-3.5 w-3.5 mr-1" /> Certificado
                </Button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Best Practices Manuals dialog
// ---------------------------------------------------------------------------
function BestPracticesDialog({ open, onOpenChange, isAdmin, canAI = false }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [aiTopic, setAiTopic] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [form, setForm] = useState({ title: "", content: "" });
  const [openForm, setOpenForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/best-practices");
      setItems(data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (open) {
      load();
      setSelected(null);
    }
  }, [open, load]);

  const generateAi = async () => {
    if (!aiTopic.trim()) {
      toast.error("Informe um tópico");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/best-practices/ai", { topic: aiTopic });
      toast.success("Manual gerado pela IA");
      setAiTopic("");
      await load();
      setSelected(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "IA indisponível");
    } finally {
      setAiLoading(false);
    }
  };

  const saveManual = async () => {
    if (!form.title.trim()) {
      toast.error("Título obrigatório");
      return;
    }
    try {
      if (editingId) {
        await api.patch(`/best-practices/${editingId}`, form);
        toast.success("Manual atualizado");
      } else {
        await api.post("/best-practices", form);
        toast.success("Manual salvo");
      }
      setOpenForm(false);
      setForm({ title: "", content: "" });
      setEditingId(null);
      await load();
      if (editingId && selected?.id === editingId) {
        setSelected((s) => ({ ...s, ...form }));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    }
  };

  const openEdit = (m) => {
    setEditingId(m.id);
    setForm({ title: m.title || "", content: m.content || "" });
    setOpenForm(true);
  };

  const remove = async (id) => {
    if (!confirm("Excluir este manual?")) return;
    try {
      await api.delete(`/best-practices/${id}`);
      load();
      setSelected(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-emerald-800" /> Manual de Boas Práticas
          </DialogTitle>
          <DialogDescription className="font-body text-xs text-stone-500">
            {canAI
              ? "Documentos de referência. Peça para a IA gerar rascunhos e edite conforme sua operação."
              : "Documentos de referência da sua operação."}
          </DialogDescription>
        </DialogHeader>

        {canAI && (
          <Card className="p-3 bg-emerald-50/50 border-emerald-200 rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-emerald-800" />
              <span className="font-display font-bold text-sm">Gerar com IA</span>
            </div>
            <div className="flex gap-2">
              <Input
                value={aiTopic}
                onChange={(e) => setAiTopic(e.target.value)}
                placeholder="Ex: Higienização de bancadas de manipulação"
                className="bg-white"
                data-testid="manual-ai-topic"
              />
              <Button
                onClick={generateAi}
                disabled={aiLoading}
                className="bg-emerald-800 hover:bg-emerald-900 text-white"
                data-testid="manual-ai-generate"
              >
                {aiLoading ? "Gerando…" : "Gerar"}
              </Button>
            </div>
          </Card>
        )}
        {isAdmin && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setEditingId(null);
              setForm({ title: "", content: "" });
              setOpenForm(true);
            }}
            className="text-xs text-emerald-800 self-start"
            data-testid="manual-manual-new"
          >
            <Plus className="h-3 w-3 mr-1" /> Criar manual manualmente
          </Button>
        )}

        <div className="grid grid-cols-3 gap-3 mt-3" style={{ minHeight: 320 }}>
          <div className="col-span-1 border-r border-stone-200 pr-3 space-y-1 overflow-y-auto">
            {items.length === 0 && (
              <div className="text-xs text-stone-500 font-body italic py-4 text-center">
                Nenhum manual ainda.
              </div>
            )}
            {items.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setSelected(m)}
                className={`w-full text-left px-3 py-2 rounded-md hover:bg-stone-50 ${
                  selected?.id === m.id ? "bg-emerald-50 border border-emerald-200" : ""
                }`}
                data-testid={`manual-item-${m.id}`}
              >
                <div className="font-display font-semibold text-sm truncate">{m.title}</div>
                <div className="text-[11px] text-stone-500 font-body">
                  {m.generated_by_ai ? "IA" : "Manual"} · {formatDateTime(m.updated_at)}
                </div>
              </button>
            ))}
          </div>
          <div className="col-span-2 min-w-0">
            {!selected && (
              <div className="text-center text-sm text-stone-500 font-body py-10">
                Selecione um manual para visualizar.
              </div>
            )}
            {selected && (
              <div>
                <div className="flex items-start gap-2 mb-2">
                  <h3 className="font-display text-xl font-bold flex-1 tracking-tight">
                    {selected.title}
                  </h3>
                  {isAdmin && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(selected)}
                        data-testid="manual-edit-btn"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-rose-600"
                        onClick={() => remove(selected.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
                <Card className="p-4 rounded-md bg-stone-50 border-stone-200 max-h-[420px] overflow-y-auto">
                  <pre className="whitespace-pre-wrap font-body text-sm text-stone-800">
                    {selected.content || "(sem conteúdo)"}
                  </pre>
                </Card>
              </div>
            )}
          </div>
        </div>

        {openForm && (
          <Dialog
            open={openForm}
            onOpenChange={(o) => {
              setOpenForm(o);
              if (!o) setEditingId(null);
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display">
                  {editingId ? "Editar manual" : "Novo manual"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Título</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="mt-1 bg-white"
                  />
                </div>
                <div>
                  <Label>Conteúdo (markdown)</Label>
                  <Textarea
                    rows={10}
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    className="mt-1 bg-white font-mono text-sm"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={saveManual}
                  className="bg-emerald-800 hover:bg-emerald-900 text-white"
                >
                  Salvar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------
function getVideoEmbed(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
}
